import BigNumber from 'bignumber.js'
import {
  SerializedFarm,
  DeserializedPool,
  SerializedPool,
  SerializedCakeVault,
  DeserializedCakeVault,
} from 'state/types'
import { deserializeToken } from 'state/user/hooks/helpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { convertSharesToCake } from 'views/Pools/helpers'

type UserData =
  | DeserializedPool['userData']
  | {
      allowance: number | string
      stakingTokenBalance: number | string
      stakedBalance: number | string
      pendingReward: number | string
    }

export const transformUserData = (userData: UserData) => {
  return {
    allowance: userData ? new BigNumber(userData.allowance) : BIG_ZERO,
    stakingTokenBalance: userData ? new BigNumber(userData.stakingTokenBalance) : BIG_ZERO,
    stakedBalance: userData ? new BigNumber(userData.stakedBalance) : BIG_ZERO,
    pendingReward: userData ? new BigNumber(userData.pendingReward) : BIG_ZERO,
  }
}

const transformProfileRequirement = (profileRequirement?: { required: boolean; thresholdPoints: string }) => {
  return profileRequirement
    ? {
        required: profileRequirement.required,
        thresholdPoints: profileRequirement.thresholdPoints
          ? new BigNumber(profileRequirement.thresholdPoints)
          : BIG_ZERO,
      }
    : undefined
}

export const transformPool = (pool: SerializedPool): DeserializedPool => {
  const {
    totalStaked,
    stakingLimit,
    numberBlocksForUserLimit,
    userData,
    stakingToken,
    earningToken,
    profileRequirement,
    startBlock,
    ...rest
  } = pool

  return {
    ...rest,
    startBlock,
    profileRequirement: transformProfileRequirement(profileRequirement),
    stakingToken: deserializeToken(stakingToken),
    earningToken: deserializeToken(earningToken),
    userData: transformUserData(userData),
    totalStaked: new BigNumber(totalStaked),
    stakingLimit: new BigNumber(stakingLimit),
    stakingLimitEndBlock: numberBlocksForUserLimit + startBlock,
  }
}

export const transformLockedVault = (vault: SerializedCakeVault): DeserializedCakeVault => {
  if (!vault) {
    // Return a default vault structure if vault is undefined
    return {
      totalShares: BIG_ZERO,
      totalLockedAmount: BIG_ZERO,
      pricePerFullShare: BIG_ZERO,
      totalCakeInVault: BIG_ZERO,
      fees: { performanceFee: 0, withdrawalFee: 0, withdrawalFeePeriod: 0, performanceFeeAsDecimal: 0 },
      userData: {
        isLoading: false,
        userShares: BIG_ZERO,
        cakeAtLastUserAction: BIG_ZERO,
        lastDepositedTime: '0',
        lastUserActionTime: '0',
        lockEndTime: '0',
        lockStartTime: '0',
        locked: false,
        lockedAmount: BIG_ZERO,
        userBoostedShare: BIG_ZERO,
        currentOverdueFee: BIG_ZERO,
        currentPerformanceFee: BIG_ZERO,
        // @ts-ignore
        balance: BIG_ZERO,
      },
    }
  }

  const {
    totalShares: totalSharesAsString,
    totalLockedAmount: totalLockedAmountAsString,
    pricePerFullShare: pricePerFullShareAsString,
    totalCakeInVault: totalCakeInVaultAsString,
    fees = {},
    userData = {},
  } = vault

  const performanceFee = (fees as any)?.performanceFee || 0
  const withdrawalFee = (fees as any)?.withdrawalFee || 0
  const withdrawalFeePeriod = (fees as any)?.withdrawalFeePeriod || 0

  const isLoading = (userData as any)?.isLoading || false
  const userSharesAsString = (userData as any)?.userShares || '0'
  const cakeAtLastUserActionAsString = (userData as any)?.cakeAtLastUserAction || '0'
  const lastDepositedTime = (userData as any)?.lastDepositedTime || '0'
  const lastUserActionTime = (userData as any)?.lastUserActionTime || '0'
  const userBoostedShareAsString = (userData as any)?.userBoostedShare || '0'
  const lockEndTime = (userData as any)?.lockEndTime || '0'
  const lockStartTime = (userData as any)?.lockStartTime || '0'
  const locked = (userData as any)?.locked || false
  const lockedAmountAsString = (userData as any)?.lockedAmount || '0'
  const currentOverdueFeeAsString = (userData as any)?.currentOverdueFee || '0'
  const currentPerformanceFeeAsString = (userData as any)?.currentPerformanceFee || '0'

  const totalShares = totalSharesAsString ? new BigNumber(totalSharesAsString) : BIG_ZERO
  const totalLockedAmount = new BigNumber(totalLockedAmountAsString)
  const pricePerFullShare = pricePerFullShareAsString ? new BigNumber(pricePerFullShareAsString) : BIG_ZERO
  const totalCakeInVault = new BigNumber(totalCakeInVaultAsString)
  const userShares = new BigNumber(userSharesAsString)
  const cakeAtLastUserAction = new BigNumber(cakeAtLastUserActionAsString)
  const lockedAmount = new BigNumber(lockedAmountAsString)
  const userBoostedShare = new BigNumber(userBoostedShareAsString)
  const currentOverdueFee = currentOverdueFeeAsString ? new BigNumber(currentOverdueFeeAsString) : BIG_ZERO
  const currentPerformanceFee = currentPerformanceFeeAsString ? new BigNumber(currentPerformanceFeeAsString) : BIG_ZERO

  const performanceFeeAsDecimal = performanceFee && performanceFee / 100

  const balance = convertSharesToCake(
    userShares,
    pricePerFullShare,
    undefined,
    undefined,
    currentOverdueFee.plus(currentPerformanceFee).plus(userBoostedShare),
  )

  return {
    totalShares,
    totalLockedAmount,
    pricePerFullShare,
    totalCakeInVault,
    fees: { performanceFee, withdrawalFee, withdrawalFeePeriod, performanceFeeAsDecimal },
    userData: {
      isLoading,
      userShares,
      cakeAtLastUserAction,
      lastDepositedTime,
      lastUserActionTime,
      lockEndTime,
      lockStartTime,
      locked,
      lockedAmount,
      userBoostedShare,
      currentOverdueFee,
      currentPerformanceFee,
      balance,
    },
  }
}

export const getTokenPricesFromFarm = (farms: SerializedFarm[]) => {
  return farms.reduce((prices, farm) => {
    const quoteTokenAddress = farm.quoteToken.address.toLocaleLowerCase()
    const tokenAddress = farm.token.address.toLocaleLowerCase()
    /* eslint-disable no-param-reassign */
    if (!prices[quoteTokenAddress]) {
      prices[quoteTokenAddress] = new BigNumber(farm.quoteTokenPriceBusd).toNumber()
    }
    if (!prices[tokenAddress]) {
      prices[tokenAddress] = new BigNumber(farm.tokenPriceBusd).toNumber()
    }
    /* eslint-enable no-param-reassign */
    return prices
  }, {})
}
